import {
  afterNextRender,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
} from '@angular/core';

interface ParsedValue {
  prefix: string;
  target: number;
  suffix: string;
  separator: '' | ',' | '.' | ' ';
  decimals: number;
}

@Directive({
  selector: '[countUp]',
})
export class CountUpDirective {
  readonly countUp = input.required<string>();
  readonly countUpDuration = input(1800);

  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    afterNextRender(() => {
      const element = this.el.nativeElement as HTMLElement;
      const parsed = this.parse(this.countUp());

      const prefersReducedMotion =
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

      if (prefersReducedMotion) {
        element.textContent = this.format(parsed.target, parsed);
        return;
      }

      element.textContent = this.format(0, parsed);

      let rafId = 0;
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            observer.unobserve(element);
            rafId = this.animate(element, parsed);
          }
        },
        { threshold: 0.3 },
      );

      observer.observe(element);

      this.destroyRef.onDestroy(() => {
        observer.disconnect();
        if (rafId) cancelAnimationFrame(rafId);
      });
    });
  }

  private parse(raw: string): ParsedValue {
    const match = raw.match(/^(\D*)([\d][\d.,\s]*)(.*)$/);
    if (!match) {
      return { prefix: '', target: 0, suffix: raw, separator: '', decimals: 0 };
    }
    const prefix = match[1] ?? '';
    const numericRaw = match[2] ?? '0';
    const suffix = match[3] ?? '';

    let separator: ParsedValue['separator'] = '';
    if (numericRaw.includes(',')) separator = ',';
    else if (/\d\s\d/.test(numericRaw)) separator = ' ';

    const decimalMatch = numericRaw.match(/\.(\d+)$/);
    const decimals = decimalMatch ? decimalMatch[1].length : 0;

    const cleaned = numericRaw.replace(/[,\s]/g, '');
    const target = Number(cleaned) || 0;

    return { prefix, target, suffix, separator, decimals };
  }

  private format(value: number, parsed: ParsedValue): string {
    const rounded = parsed.decimals > 0
      ? value.toFixed(parsed.decimals)
      : Math.round(value).toString();

    let formatted = rounded;
    if (parsed.separator) {
      const [intPart, decPart] = rounded.split('.');
      const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, parsed.separator);
      formatted = decPart ? `${withSep}.${decPart}` : withSep;
    }

    return `${parsed.prefix}${formatted}${parsed.suffix}`;
  }

  private animate(element: HTMLElement, parsed: ParsedValue): number {
    const duration = this.countUpDuration();
    const start = performance.now();
    let rafId = 0;

    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = parsed.target * eased;
      element.textContent = this.format(current, parsed);
      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        element.textContent = this.format(parsed.target, parsed);
      }
    };

    rafId = requestAnimationFrame(tick);
    return rafId;
  }
}
